import {
  claimStatusErrors,
  ClaimStatuses
} from "@libs/gateways/acc/AccGateway.dtos.ts";
import {
  ContactResultItemDto,
  ContactType,
  OrganisationContactDto
} from "@libs/gateways/practice/PracticeGateway.dtos.ts";
import { filterInsurers } from "@modules/practice/screens/shared-components/utils/contact.utils.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { Item } from "@ui-components/filter-bar/FilterBar.types.ts";
import { usePatientLabel } from "@ui-components/hooks/usePatientLabel.ts";

import { ClaimsFilterState } from "./claims-filter.types.ts";

export const useClaimsFilterItems = (): Item<ClaimsFilterState>[] => {
  return [
    useClaimsListFilterContactPicker(),
    useClaimsListFilterClaimPicker(),
    useClaimsListFilterProviderPicker(),
    useClaimsListFilterInsurerPicker(),
    useClaimsFilterStatusPicker(),
    useClaimsFilterDatesPicker()
  ];
};

export const useClaimsListFilterContactPicker = (): Item<ClaimsFilterState> => {
  const patientLabel = usePatientLabel();
  return {
    type: "contactPicker",
    name: "patients",
    stickItem: true,
    props: {
      styles: { root: { width: 400, minWidth: 400 } },
      inputProps: { placeholder: `Search for ${patientLabel}` }
    }
  };
};

export const useClaimsListFilterClaimPicker = (): Item<ClaimsFilterState> => {
  return {
    name: "claimNumber",
    type: "searchBox",
    stickItem: true,
    props: {
      placeholder: "Search by claim number",
      styles: { root: { maxWidth: 250, minWidth: 250 } }
    }
  };
};

export const useClaimsListFilterProviderPicker =
  (): Item<ClaimsFilterState> => {
    return {
      type: "bookableUsersSelect",
      name: "providers",
      props: {
        id: "claim-filter-providers",
        multiSelect: true,
        placeholder: "Provider",
        calloutWidth: 250
      }
    };
  };

const useClaimsListFilterInsurerPicker = (): Item<ClaimsFilterState> => {
  return {
    type: "contactsSelect",
    name: "insurers",
    props: {
      id: "claim-filter-insurers",
      multiSelect: true,
      placeholder: "Insurer",
      calloutWidth: 250,
      contactFilter: { types: [ContactType.Organisation] },
      resultFilterPredicate: (contact: ContactResultItemDto) => {
        const organisation = contact.value as OrganisationContactDto;
        return filterInsurers(organisation, false);
      }
    }
  };
};

const useClaimsFilterStatusPicker = (): Item<ClaimsFilterState> => {
  const { acc } = useStores();

  const statusesRefData = acc.ref.claimStatuses;

  const filteredOptions = statusesRefData.keyTextValues.filter(
    x =>
      x.key !== ClaimStatuses.Ready &&
      x.key !== ClaimStatuses.Error &&
      !claimStatusErrors.includes(x.key)
  );

  const options = [
    {
      key: ClaimStatuses.Ready,
      text: statusesRefData.get(ClaimStatuses.Ready)?.text ?? "Ready",
      isDivider: true
    },
    ...filteredOptions
      .sort((a, b) => a.text.localeCompare(b.text))
      .map((x, i) => {
        if (i + 1 === filteredOptions.length) {
          return { ...x, isDivider: true };
        }
        return x;
      }),
    {
      key: ClaimStatuses.AuthenticationError,
      text:
        statusesRefData.get(ClaimStatuses.AuthenticationError)?.text ??
        "Authentication error"
    },
    {
      key: ClaimStatuses.CommunicationError,
      text:
        statusesRefData.get(ClaimStatuses.CommunicationError)?.text ??
        "Communication error"
    },
    {
      key: ClaimStatuses.Error,
      text: statusesRefData.get(ClaimStatuses.Error)?.text ?? "Lodge error"
    },
    {
      key: ClaimStatuses.ProcessingError,
      text:
        statusesRefData.get(ClaimStatuses.ProcessingError)?.text ??
        "Processing error"
    },
    {
      key: ClaimStatuses.GetStatusError,
      text:
        statusesRefData.get(ClaimStatuses.GetStatusError)?.text ?? "Query error"
    },
    {
      key: ClaimStatuses.ValidationError,
      text:
        statusesRefData.get(ClaimStatuses.ValidationError)?.text ??
        "Validation error"
    }
  ];

  return {
    type: "optionsSelect",
    name: "claimStatusCode",
    props: {
      id: "claim-filter-status",
      placeholder: "Status",
      options,
      multiSelect: true,
      hideSearchOption: true,
      calloutWidth: 300
    }
  };
};

export const useClaimsFilterDatesPicker = (): Item<ClaimsFilterState> => {
  return {
    type: "datesRangePicker",
    name: "datesRangePicker",
    props: {
      id: "claims-filter-dates-range",
      startDateProps: { id: "claims-filter-start-date" },
      endDateProps: { id: "claims-filter-end-date" },
      startDateName: "dateFrom",
      endDateName: "dateTo",
      placeholder: "Date(s)"
    }
  };
};
